/**
 * Functions for evaluating arc paths. The results are all discretized, not analytical arcs.
 * @module
 */
import type { PolyLine2 } from "./polyLine2Types";
import { polyLine2New } from "./polyLine2Types";
import type { Vec2 } from "./vec2";
import { vec2Dist } from "./vec2";

const { max, min, cos, sin, acos, abs } = Math;

export function ellipticalArc2ByCenterStartEnd(
  x: number,
  y: number,
  xRadius: number,
  yRadius: number,
  startAngleRad: number,
  endAngleRad: number,
  clockwise: boolean,
): Vec2[] {
  const curve: Vec2[] = [];
  const startRad = !clockwise ? startAngleRad : endAngleRad;
  const endRad = !clockwise ? endAngleRad : startAngleRad;
  const numPoints = 1 + Math.ceil(Math.abs(endRad - startRad) / (Math.PI / 18));
  const radStep = (endRad - startRad) / (numPoints - 1);
  for (let i = 0; i < numPoints; i++) {
    const angle = startRad + i * radStep;
    const x1 = x + xRadius * Math.cos(angle);
    const y1 = y + yRadius * Math.sin(angle);
    curve.push([x1, y1]);
  }
  return curve;
}

export function arc2ByCenterStartAngle(
  centerX: number,
  centerY: number,
  startX: number,
  startY: number,
  angle: number,
  chordTol: number = 0.01,
  minDivs: number = 10,
  maxDivs: number = 128,
): PolyLine2 {
  const radius = vec2Dist(centerX, centerY, startX, startY);

  const angStep = 2 * acos(1 - chordTol / radius);
  let numSegs = abs(angle / angStep + 1) | 0;

  numSegs = min(max(numSegs, minDivs), maxDivs);

  const theta = angle / numSegs;

  const c = cos(theta);
  const s = sin(theta);
  let t = 0;

  let x = startX - centerX;
  let y = startY - centerY;

  const r = polyLine2New((numSegs + 1) * 2);
  const c2 = (numSegs + 1) * 2;
  const cx = centerX;
  const cy = centerY;

  for (let i = 0; i < c2; i += 2) {
    r[i] = x + cx;
    r[i + 1] = y + cy;

    // apply the rotation matrix
    t = x;
    x = c * x - s * y;
    y = s * t + c * y;
  }

  return r;
}

export function arc2ByThreePoints(
  startX: number,
  startY: number,
  midX: number,
  midY: number,
  endX: number,
  endY: number,
  chordTol: number = 0.01,
  minDivs: number = 10,
  maxDivs: number = 128,
): PolyLine2 {
  // Calculate midpoints
  const midPoint1 = [(startX + midX) / 2, (startY + midY) / 2];
  const midPoint2 = [(midX + endX) / 2, (midY + endY) / 2];

  let centerX, centerY;

  if (startX === midX && midX === endX) {
    // Points are vertically aligned
    centerX = startX;
    centerY = (startY + endY) / 2;
  } else if (startY === midY) {
    // start to mid is horizontal
    centerX = midPoint1[0];
    const perpSlope2 = -(endX - midX) / (endY - midY);
    centerY = perpSlope2 * centerX + (midPoint2[1] - perpSlope2 * midPoint2[0]);
  } else if (midY === endY) {
    // mid to end is horizontal
    centerX = midPoint2[0];
    const perpSlope1 = -(midX - startX) / (midY - startY);
    centerY = perpSlope1 * centerX + (midPoint1[1] - perpSlope1 * midPoint1[0]);
  } else {
    const slope1 = (midY - startY) / (midX - startX);
    const slope2 = (endY - midY) / (endX - midX);

    const perpSlope1 = -1 / slope1;
    const perpSlope2 = -1 / slope2;

    const b1 = midPoint1[1] - perpSlope1 * midPoint1[0];
    const b2 = midPoint2[1] - perpSlope2 * midPoint2[0];

    centerX = (b2 - b1) / (perpSlope1 - perpSlope2);
    centerY = perpSlope1 * centerX + b1;
  }

  const startAngle = Math.atan2(startY - centerY, startX - centerX);
  const endAngle = Math.atan2(endY - centerY, endX - centerX);
  const midAngle = Math.atan2(midY - centerY, midX - centerX);

  let angle;
  if ((startAngle < midAngle && midAngle < endAngle) || (startAngle > midAngle && midAngle > endAngle)) {
    angle = endAngle - startAngle;
  } else {
    angle = endAngle - startAngle + (endAngle < startAngle ? 2 * Math.PI : -2 * Math.PI);
  }
  return arc2ByCenterStartAngle(centerX, centerY, startX, startY, angle, chordTol, minDivs, maxDivs);
}
